import * as XLSX from "xlsx";
import getCoordinates from "./getCoordinates";

/**
 * Maps the human-readable column headers used in the seizure-case Excel
 * template to the camelCase keys expected by geocodeRecords.ts and the
 * backend's SeizureCaseService (bulkUpload/createRecord). Lookup is
 * case-insensitive so "Location", "location", and "LOCATION" all resolve
 * the same way. Headers already in camelCase pass through unchanged.
 */
const HEADER_ALIASES: Record<string, string> = {
  "case no.":              "caseNo",
  "case no":               "caseNo",
  "date of seizure":       "dateOfSeizure",
  "name of accused":       "nameOfAccused",
  "gender":                "gender",
  "nationality":           "nationality",
  "location":              "location",
  "country":               "country",
  "latitude":              "latitude",
  "longitude":             "longitude",
  "charge 1":              "charge1",
  "charge 2":              "charge2",
  "charge 3":              "charge3",
  "charge 4":              "charge4",
  "charge 5":              "charge5",
  "alternative charge 1":  "alternativeCharge1",
  "alternative charge 2":  "alternativeCharge2",
  "alternative charge 3":  "alternativeCharge3",
  "species":               "species",
  "exhibit":               "exhibit",
  "weight":                "weight",
  "judgement date":        "judgmentDate",
  "judgment date":         "judgmentDate",
  "judgement outcome":     "judgmentOutcome",
  "judgment outcome":      "judgmentOutcome",
  "sentencing":            "sentencing",
  "case status":           "caseStatus",
  "no":                    "no",
};

function normalizeHeader(header: string): string {
  const raw = String(header ?? "").trim();
  return HEADER_ALIASES[raw.toLowerCase()] ?? raw;
}

/**
 * Excel's used range often extends past the last row that actually has
 * data (stray formatting, a stray space in a far column, etc.), so
 * sheet_to_json returns trailing "phantom" rows with nothing in them.
 * Treat a row as empty — and skip it — only if every cell is
 * null/undefined/blank; a row with even one real value is real data.
 */
function isRowEmpty(row: any[]): boolean {
  if (!row || row.length === 0) return true;
  return row.every((cell) => cell === undefined || cell === null || String(cell).trim() === "");
}

/** Format a JS Date → "YYYY-MM-DD" */
function isoDate(d: Date): string {
  const y  = d.getFullYear();
  const m  = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${dd}`;
}

/**
 * Resolve an Excel date cell to an ISO string.
 *
 * SheetJS with cellDates:true already converts date cells to JS Date
 * objects (cell.v instanceof Date).  If for some reason the value is
 * still a raw serial number we fall back to XLSX.SSF.parse_date_code.
 * String values are passed through (they may already be formatted).
 */
function resolveDateCell(cell: any): string | null {
  if (!cell || cell.v == null) return null;

  // ── cellDates:true path — v is already a JS Date ──────────────
  if (cell.v instanceof Date) {
    return isoDate(cell.v);
  }

  // ── Raw Excel serial number ────────────────────────────────────
  if (typeof cell.v === "number" && cell.v > 0) {
    try {
      const p = XLSX.SSF.parse_date_code(cell.v);
      if (p && p.y > 1900) {
        return `${p.y}-${String(p.m).padStart(2, "0")}-${String(p.d).padStart(2, "0")}`;
      }
    } catch { /* fall through */ }
  }

  // ── String — try to parse, otherwise return as-is ─────────────
  if (typeof cell.v === "string" && cell.v.trim()) {
    const parsed = new Date(cell.v.trim());
    if (!isNaN(parsed.getTime())) return isoDate(parsed);
    return cell.v.trim(); // preserve original string if unparseable
  }

  return null;
}

async function excelToJson(file: File): Promise<any[]> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = async (event) => {
      try {
        const data = new Uint8Array(event.target?.result as ArrayBuffer);
        const workbook = XLSX.read(data, {
          type: "array",
          cellDates: true,   // converts numeric date serials → JS Date objects
          cellNF: true,
          raw: true,         // keep raw values so we can inspect cell.v reliably
        });

        const firstSheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[firstSheetName];
        const jsonData = XLSX.utils.sheet_to_json(worksheet, {
          header: 1,
          raw: true,
        });

        const headers = jsonData[0] as string[];
        // Keep each row's original sheet position (rowIndex) so the date-cell
        // lookup below still points at the right cell address, but drop rows
        // that carry no real data — those are phantom rows past the true
        // end of the dataset, not actual records.
        const rows = (jsonData.slice(1) as any[][])
          .map((row, i) => ({ row, rowIndex: i }))
          .filter(({ row }) => !isRowEmpty(row));

        const result = await Promise.all(
          rows.map(async ({ row, rowIndex }) => {
            const rowObject: Record<string, any> = {};

            headers.forEach((header, colIndex) => {
              const key = normalizeHeader(header);
              if (key === "dateOfSeizure") {
                // Always go through the cell directly for date columns
                const cellAddress = XLSX.utils.encode_cell({
                  c: colIndex,
                  r: rowIndex + 1,
                });
                const cell = worksheet[cellAddress];
                rowObject[key] = resolveDateCell(cell) ?? "N/A";
              } else {
                rowObject[key] = row[colIndex] ?? "N/A";
              }
            });

            if (rowObject["no"]) {
              rowObject["no"] = "" + rowObject["no"];
            }

            /*if (
              rowObject["location"] &&
              rowObject["location"] !== "0" &&
              rowObject["location"] !== "N/A"
            ) {
              try {
                const coordinates = await getCoordinates(rowObject["location"]);
                rowObject["latitude"] = coordinates.lat;
                rowObject["longitude"] = coordinates.lon;
              } catch (error) {
                rowObject["latitude"] = null;
                rowObject["longitude"] = null;
                console.error(
                  `Failed to geocode location: ${rowObject["location"]}`,
                  error
                );
              }
            } else {
              rowObject["latitude"] = null;
              rowObject["longitude"] = null;
            } */

            return rowObject;
          })
        );

        resolve(result);
      } catch (error) {
        reject(error);
      }
    };

    reader.onerror = (error) => {
      reject(error);
    };

    reader.readAsArrayBuffer(file);
  });
}

export default excelToJson;
